import React from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import { useStoreState, useStoreActions } from '../store';
import { useLocale } from '../hooks/useLocale';
import { TimelineControl } from './TimelineControl';
import { VolumeControl } from './VolumeControl';
import { FullscreenControl } from './FullscreenControl';
import { PlayerStatus, VideoType } from '../typings';
import { formatSeconds } from '../utils/format-seconds';
import { theme } from '../utils/theme';
import playIcon from '../assets/images/play.svg';
import pauseIcon from '../assets/images/pause.svg';
import languageIcon from '../assets/images/language.svg';
import modifierIcon from '../assets/images/modifier.svg';

export const BottomControls = () => {
  const [switchingVideo, setSwitchingVideo] = React.useState(false);
  const locale = useLocale();
  const videoData = useStoreState(state => state.videos.data);
  const videoItem = useStoreState(state => state.player.videoItem);
  const modifierVideo = useStoreState(state => state.videos.modifierVideo);
  const status = useStoreState(state => state.player.status);
  const controlsVisible = useStoreState(state => state.player.controlsVisible);
  const playbackCurrentTime = useStoreState(state => state.player.playbackCurrentTime);
  const playbackDurationTime = useStoreState(state => state.player.playbackDurationTime);
  const languageMenuVisible = useStoreState(state => state.player.languageMenuVisible);
  const subtitleOptionsVisible = useStoreState(state => state.player.subtitleOptionsVisible);
  const castSession = useStoreState(state => state.chromecast.castSession);
  const actions = useStoreActions(actions => actions);

  function playButtonVisible() {
    let result = true;

    // Hide while casting
    if (castSession) {
      result = false;
    }

    return result;
  }

  function getNowPlayingText() {
    if (castSession) {
      return locale.translations.BottomControls.PlayingOn(castSession.getCastDevice().friendlyName);
    }

    return locale.translations.BottomControls.NowPlaying;
  }

  function handlePlayClick() {
    if (castSession) {
      actions.chromecast.playOrPause();
      return;
    }
    if (status === PlayerStatus.Playing) {
      actions.player.pause();
      actions.interactions.pause();
    } else {
      actions.player.play();
      actions.interactions.play();
    }
  }

  async function handleModifierClick() {
    if (switchingVideo) return;

    setSwitchingVideo(true);

    const { workoutVideo, modifierVideo } = await actions.videos.fetchData();

    if (!videoItem || !workoutVideo || !modifierVideo) return;

    if (videoItem.type === VideoType.MODIFIER) {
      actions.videos.switchVideo(workoutVideo);
    } else {
      actions.videos.switchVideo(modifierVideo);
    }

    setSwitchingVideo(false);
  }

  function handleLanguageControlClick() {
    actions.player.setLanguageMenuVisible(!languageMenuVisible);
  }

  return (
    <Container className={classnames('bottom-controls', { visible: controlsVisible || castSession })}>
      <NowPlayingText>{getNowPlayingText()}</NowPlayingText>

      <VideoTitle>{videoData?.metadata.title}</VideoTitle>

      <TimelineControl />
      <BottomContainer>
        <LeftContent>
          {playButtonVisible() && <PlayControl onClick={handlePlayClick} isPlaying={status === PlayerStatus.Playing} />}

          <TimerControl>
            {formatSeconds(playbackCurrentTime)} / {formatSeconds(playbackDurationTime)}
          </TimerControl>

          <VolumeControl />

          <Spacer />
        </LeftContent>

        <RightContent>
          {modifierVideo && (
            <Icon icon={modifierIcon} active={videoItem?.type === VideoType.MODIFIER} onClick={handleModifierClick} />
          )}

          <Icon
            icon={languageIcon}
            active={languageMenuVisible || subtitleOptionsVisible}
            onClick={handleLanguageControlClick}
          />

          <FullscreenControl />
        </RightContent>
      </BottomContainer>
    </Container>
  );
};

const NowPlayingText = styled.div`
  font-family: 'Lato-Bold', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.primaryBlue};
  text-transform: uppercase;

  @media ${theme.mobileMediaQuery} {
    font-size: 10px;
    line-height: 1.6;
  }
`;

const VideoTitle = styled.div`
  font-family: 'Lato-Black', sans-serif;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.3;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.white};
  margin: 8px 0 30px;

  @media ${theme.mobileMediaQuery} {
    font-size: 16px;
    max-width: 265px;
    margin: 5px 0 20px;
  }
`;

const Container = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-start;
  justify-content: start;
  flex-direction: column;
  padding: 0 32px;

  @media ${theme.mobileMediaQuery} {
    padding: 0 24px;
  }
`;

const BaseControl = styled.div<{ active?: boolean }>`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      opacity: 1;
    }
  }

  @media ${theme.mobileMediaQuery} {
    width: 18px;
    height: 18px;
  }
`;

const PlayControl = styled(BaseControl)<{ isPlaying: boolean }>`
  background: url(${({ isPlaying }) => (isPlaying ? pauseIcon : playIcon)}) no-repeat center / contain;
`;

const Icon = styled(BaseControl)<{ icon: string }>`
  background: url(${({ icon }) => icon}) no-repeat center / contain;
`;

const TimerControl = styled(BaseControl)`
  user-select: none;
  font-family: 'Lato-Light', sans-serif;
  font-size: 20px;
  line-height: 1.5;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.white};
  width: fit-content;
  cursor: initial;

  @media ${theme.mobileMediaQuery} {
    font-size: 12px;
    letter-spacing: 0.3px;
    text-align: center;
    width: fit-content;
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const BottomContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 34px 0;

  @media ${theme.mobileMediaQuery} {
    padding: 30px 0;
  }
`;

const LeftContent = styled.div`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 20px;

  @media ${theme.mobileMediaQuery} {
    grid-column-gap: 10px;
  }
`;

const RightContent = styled.div`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 32px;

  @media ${theme.mobileMediaQuery} {
    grid-column-gap: 24px;
  }
`;
